import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, AlertTriangle, CheckCircle2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../components/ui/utils";
import { useInViewOnce } from "../../../hooks/useInViewOnce";

type Risk = "ok" | "attention" | "risk";

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const STUDENTS = [
  { id: 1, initials: "AM", risk: "ok" as Risk, vark: "Visual", subject: "Matemática", score: 8.5, action: "Avançar para próximo módulo" },
  { id: 2, initials: "BR", risk: "risk" as Risk, vark: "Cinestésico", subject: "Português", score: 4.2, action: "Reforço urgente em interpretação" },
  { id: 3, initials: "CS", risk: "ok" as Risk, vark: "Leitura", subject: "Biologia", score: 9.1, action: "Trilha avançada disponível" },
  { id: 4, initials: "DO", risk: "attention" as Risk, vark: "Auditivo", subject: "História", score: 6.3, action: "Aumentar frequência de revisão" },
  { id: 5, initials: "EF", risk: "ok" as Risk, vark: "Visual", subject: "Matemática", score: 8.0, action: "Priorizar exercícios práticos" },
  { id: 6, initials: "FM", risk: "risk" as Risk, vark: "Auditivo", subject: "Matemática", score: 3.8, action: "Intervenção precoce necessária" },
  { id: 7, initials: "GR", risk: "attention" as Risk, vark: "Cinestésico", subject: "Física", score: 6.8, action: "Revisar conceitos de força" },
  { id: 8, initials: "HC", risk: "ok" as Risk, vark: "Leitura", subject: "Português", score: 7.9, action: "Consistência boa — manter" },
  { id: 9, initials: "IB", risk: "attention" as Risk, vark: "Visual", subject: "Química", score: 5.9, action: "Reforçar base de estequiometria" },
  { id: 10, initials: "JK", risk: "ok" as Risk, vark: "Auditivo", subject: "Biologia", score: 8.8, action: "Manter ritmo atual" },
  { id: 11, initials: "LA", risk: "risk" as Risk, vark: "Leitura", subject: "História", score: 4.5, action: "Contato com família recomendado" },
  { id: 12, initials: "MN", risk: "ok" as Risk, vark: "Visual", subject: "Física", score: 7.6, action: "Revisar trigonometria antes da prova" },
];

const RISK_STYLE = {
  ok: {
    circle: "bg-teal-500/20 border-teal-400/50 text-teal-200",
    dot: "bg-teal-400",
    badge: "border-teal-400/30 bg-teal-500/10 text-teal-300",
    label: "No ritmo",
  },
  attention: {
    circle: "bg-amber-500/20 border-amber-400/50 text-amber-200",
    dot: "bg-amber-400",
    badge: "border-amber-400/30 bg-amber-500/10 text-amber-300",
    label: "Atenção",
  },
  risk: {
    circle: "bg-rose-500/20 border-rose-400/50 text-rose-200",
    dot: "bg-rose-400",
    badge: "border-rose-400/30 bg-rose-500/10 text-rose-300",
    label: "Em risco",
  },
};

const VARK_COLORS: Record<string, string> = {
  Visual: "text-cyan-300 bg-cyan-500/10 border-cyan-400/30",
  Auditivo: "text-violet-300 bg-violet-500/10 border-violet-400/30",
  Leitura: "text-sky-300 bg-sky-500/10 border-sky-400/30",
  Cinestésico: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
};

type DashboardPreviewProps = {
  onDemoCta: () => void;
};

export function DashboardPreview({ onDemoCta }: DashboardPreviewProps) {
  const { ref, isVisible } = useInViewOnce();
  const [withNeuroment, setWithNeuroment] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected = STUDENTS.find((s) => s.id === selectedId) ?? null;
  const okCount = STUDENTS.filter((s) => s.risk === "ok").length;
  const attCount = STUDENTS.filter((s) => s.risk === "attention").length;
  const riskCount = STUDENTS.filter((s) => s.risk === "risk").length;

  const handleToggle = (val: boolean) => {
    setWithNeuroment(val);
    if (!val) setSelectedId(null);
  };

  const handleTileClick = (id: number) => {
    if (!withNeuroment) return;
    setSelectedId((prev) => (prev === id ? null : id));
  };

  return (
    <section
      id="simulador"
      ref={ref}
      className="scroll-mt-24 border-b border-white/[0.06] py-12 sm:py-16 lg:py-20"
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0, 0, 0.2, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <span className="mb-4 inline-block rounded-full border border-teal-400/30 bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-300 sm:text-sm">
            Visão do coordenador
          </span>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
            Como a coordenação enxerga{" "}
            <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">
              a turma em tempo real
            </span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
            Sem o NeuroMent, nenhum dado. Com ele, risco, perfil e ação recomendada para cada aluno — antes que o problema vire acúmulo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.65, delay: 0.15, ease: SPRING }}
          className="mx-auto mt-10 max-w-3xl sm:mt-12"
        >
          {/* Toggle */}
          <div className="mb-6 flex justify-center sm:mb-8">
            <div className="flex rounded-xl border border-white/10 bg-white/[0.04] p-1 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => handleToggle(false)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
                  !withNeuroment
                    ? "bg-slate-700/80 text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-300"
                )}
              >
                <EyeOff className="h-3.5 w-3.5" aria-hidden />
                Sem NeuroMent
              </button>
              <button
                type="button"
                onClick={() => handleToggle(true)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200",
                  withNeuroment
                    ? "bg-teal-500/20 text-teal-200 shadow-sm ring-1 ring-teal-400/30"
                    : "text-slate-400 hover:text-slate-300"
                )}
              >
                <Eye className="h-3.5 w-3.5" aria-hidden />
                Com NeuroMent
              </button>
            </div>
          </div>

          {/* Dashboard card */}
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#080f1c] p-4 sm:p-6">

            {/* Class header row */}
            <div className="mb-4 flex items-center justify-between gap-3 sm:mb-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  3º Ano B — Turno Matutino
                </p>
                <p className="mt-0.5 text-sm font-medium text-white">
                  12 alunos · {withNeuroment ? "Dados em tempo real" : "Sem dados disponíveis"}
                </p>
              </div>
              <AnimatePresence mode="wait">
                {withNeuroment ? (
                  <motion.div
                    key="live"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    className="flex items-center gap-1.5 rounded-full border border-teal-400/30 bg-teal-500/10 px-2.5 py-1 text-xs font-medium text-teal-300"
                  >
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal-400" />
                    Ao vivo
                  </motion.div>
                ) : (
                  <motion.div
                    key="off"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-full border border-slate-600/50 bg-slate-800/50 px-2.5 py-1 text-xs text-slate-500"
                  >
                    Sem acesso
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Student grid */}
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 sm:gap-3">
              {STUDENTS.map((student, i) => {
                const style = RISK_STYLE[student.risk];
                const isSelected = selectedId === student.id;

                return (
                  <motion.button
                    key={student.id}
                    type="button"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={isVisible ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.4, ease: SPRING, delay: 0.2 + i * 0.04 }}
                    onClick={() => handleTileClick(student.id)}
                    disabled={!withNeuroment}
                    className={cn(
                      "group relative flex flex-col items-center gap-1.5 rounded-xl border p-2.5 transition-all duration-200 sm:p-3",
                      withNeuroment
                        ? cn(
                            style.circle,
                            "cursor-pointer",
                            isSelected
                              ? "ring-2 ring-white/20 ring-offset-1 ring-offset-[#080f1c]"
                              : "hover:-translate-y-0.5 hover:brightness-110"
                          )
                        : "border-slate-700/50 bg-slate-800/30 text-slate-600 cursor-default"
                    )}
                    aria-label={withNeuroment ? `${student.initials}: ${style.label}` : student.initials}
                  >
                    <span className="text-sm font-semibold sm:text-base">{student.initials}</span>
                    {withNeuroment ? (
                      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} aria-hidden />
                    ) : (
                      <span className="text-[0.6rem] text-slate-600">—</span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Selected student detail */}
            <AnimatePresence>
              {selected && withNeuroment && (
                <motion.div
                  key={selected.id}
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3 sm:p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-sm font-bold", RISK_STYLE[selected.risk].circle)}>
                          {selected.initials}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[0.65rem] font-medium", RISK_STYLE[selected.risk].badge)}>
                              {RISK_STYLE[selected.risk].label}
                            </span>
                            <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[0.65rem] font-medium", VARK_COLORS[selected.vark] ?? "")}>
                              {selected.vark}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-400">
                            {selected.subject} · média {selected.score.toFixed(1)}
                          </p>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1 text-right sm:text-right">
                        <p className="text-[0.7rem] uppercase tracking-wide text-slate-500">Recomendação IA</p>
                        <p className="mt-0.5 text-sm font-medium text-white">{selected.action}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Summary stats */}
            <div className="mt-4 border-t border-white/[0.06] pt-4 sm:mt-5 sm:pt-5">
              <AnimatePresence mode="wait">
                {withNeuroment ? (
                  <motion.div
                    key="stats-on"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-wrap items-center justify-center gap-3 sm:gap-6"
                  >
                    <span className="flex items-center gap-1.5 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-teal-400" />
                      <span className="font-semibold text-teal-300">{okCount}</span>
                      <span className="text-slate-400">no ritmo</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-sm">
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                      <span className="font-semibold text-amber-300">{attCount}</span>
                      <span className="text-slate-400">atenção</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-sm">
                      <TrendingUp className="h-4 w-4 rotate-180 text-rose-400" />
                      <span className="font-semibold text-rose-300">{riskCount}</span>
                      <span className="text-slate-400">em risco</span>
                    </span>
                  </motion.div>
                ) : (
                  <motion.p
                    key="stats-off"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-sm text-slate-600"
                  >
                    12 alunos · 0 perfis identificados · 0 riscos visíveis
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-6 text-center sm:mt-8"
          >
            <Button
              onClick={onDemoCta}
              className="landing-cta-glow bg-gradient-to-r from-teal-500 to-cyan-600 font-semibold hover:from-teal-400 hover:to-cyan-500"
            >
              Ver demonstração completa
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Button>
            <p className="mt-3 text-xs text-slate-500">
              {withNeuroment
                ? "Clique em qualquer aluno para ver o perfil e a recomendação da IA"
                : "Ative o NeuroMent acima para ver os dados da turma"}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
